import React, { useEffect, useRef, useState } from "react";
import { api, type Narrator } from "../api.js";

/**
 * Who reads the narration.
 *
 * Listed rather than typed. A voice is a twenty-character opaque id, so a text
 * field asking for one is a field nobody can fill without leaving for another
 * website — which is why, until now, the only people who ever set a narrator
 * were the ones who already knew an id by heart.
 *
 * Every entry can be heard before it is chosen. A name and three adjectives
 * are not enough to pick a voice by, and the alternative is choosing one,
 * waiting ten minutes for a film, and finding out then.
 */
export const NarratorPicker: React.FC<{
  value: string | undefined;
  onChange: (voiceId: string | undefined) => void;
}> = ({ value, onChange }) => {
  const [narrators, setNarrators] = useState<Narrator[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .listNarrators()
      .then((response) => {
        if (!cancelled) setNarrators(response.narrators);
      })
      .catch((cause: Error) => {
        if (!cancelled) setError(cause.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // One at a time, and silence on the way out: a preview still playing after
  // the page has gone is a sound with no visible source.
  useEffect(
    () => () => {
      audio.current?.pause();
      audio.current = null;
    },
    [],
  );

  const preview = (narrator: Narrator) => {
    audio.current?.pause();
    if (playing === narrator.id || !narrator.previewUrl) {
      setPlaying(null);
      return;
    }
    const element = new Audio(narrator.previewUrl);
    element.onended = () => setPlaying(null);
    audio.current = element;
    setPlaying(narrator.id);
    void element.play().catch(() => setPlaying(null));
  };

  if (error) {
    return (
      <p className="text-[11.5px] leading-[1.5] text-ink-400">
        {`Narrators could not be listed (${error}). Videos will use whichever voice the deployment is configured with.`}
      </p>
    );
  }

  if (!narrators) return <p className="text-[11.5px] text-ink-400">Loading narrators…</p>;

  if (narrators.length === 0) {
    return (
      <p className="text-[11.5px] leading-[1.5] text-ink-400">No narrators are available on this ElevenLabs account.</p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => onChange(undefined)}
        aria-pressed={!value}
        className={`rounded-[8px] border p-2.5 text-left transition-colors ${
          !value ? "border-ember-500 bg-ember-500/5" : "border-[var(--edge)] hover:border-[var(--edge-strong)]"
        }`}
      >
        <span className="block text-[12.5px] font-medium text-ink-100">Whichever is configured</span>
        <span className="block text-[11px] text-ink-300">The deployment&rsquo;s own default voice.</span>
      </button>

      <div className="flex max-h-[320px] flex-col gap-1.5 overflow-y-auto">
        {narrators.map((narrator) => {
          const chosen = value === narrator.id;
          return (
            <div
              key={narrator.id}
              className={`flex items-center gap-2 rounded-[8px] border p-2.5 transition-colors ${
                chosen ? "border-ember-500 bg-ember-500/5" : "border-[var(--edge)]"
              }`}
            >
              <button type="button" onClick={() => onChange(narrator.id)} className="min-w-0 flex-1 text-left">
                <span className="block truncate text-[12.5px] font-medium text-ink-100">{narrator.name}</span>
                {narrator.description ? (
                  <span className="block truncate text-[11px] text-ink-300">{narrator.description}</span>
                ) : null}
              </button>

              {narrator.previewUrl ? (
                <button
                  type="button"
                  onClick={() => preview(narrator)}
                  aria-label={playing === narrator.id ? `Stop ${narrator.name}` : `Listen to ${narrator.name}`}
                  className="btn btn-secondary shrink-0 px-2.5 text-[11.5px]"
                >
                  {playing === narrator.id ? "Stop" : "Listen"}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
